/**
 * Mood Animation Applicator
 * Applies mood states to avatar with ADDITIVE blending (true overlay on other animations)
 * Moods blend with lip-sync, idle, and other animations instead of replacing them
 *
 * Mouth-family ownership (Aug 29 2026 — see mouthMorphSafety.ts):
 *   - Speech viseme RAF owns smile/jaw while speaking.
 *   - This overlay owns one exclusive smile slot per corner while a mood
 *     is active (never Left AND _L AND *old at once).
 *   - Listening / thinking / idle-noise writers must overlay-account and
 *     sit under SAFE_NONSPEECH_SMILE_MAX unless this overlay is higher.
 */

import * as THREE from 'three';
import { MOOD_STATES } from '../animations/moodStates';
import type { Sentiment } from './sentimentAnalyzer';
import { getSentimentExpressivenessCap } from './sentimentAnalyzer';
import { resolveMorphTargetIndex } from '../converter';
import {
  isSmileMorphName,
  pickExclusiveSmileSlots,
  smileWeightFromBlendshapes,
} from './mouthMorphSafety';

type MoodOverlayHandle = {
  stop: () => void;
  tick: () => void;
  mood: Sentiment;
  blendshapes: Record<string, number>;
  /** True when the overlay was applied with decay options (a sentiment mood). */
  hasDecay: boolean;
  /**
   * Bring the fade-out forward to "now" (user started talking, etc.).
   * No-op if the overlay is already fading or disposed.
   */
  beginEarlyFade: (fadeDurationMs?: number) => void;
};

/**
 * Optional hold-then-fade envelope for a mood overlay. Without it a mood is
 * PINNED indefinitely (absolute-set every frame until the next mood arrives).
 * That pinning is correct for the Avatar Editor preview, but for
 * conversation-sentiment moods it made a single "positive" result hold a big
 * smile through listening / thinking / idle for the rest of the turn — the
 * Aug 16 2026 Visual IVR "silly smile that never leaves" defect. Sentiment
 * callers pass a decay so the expression reads as a reaction, not a mask.
 */
export type MoodOverlayDecayOptions = {
  /** How long to hold at full weight after the fade-in completes. */
  decayAfterMs?: number;
  /** Fade-out duration once the hold expires. Default 1500ms. */
  decayDurationMs?: number;
  /** Called once when the overlay has fully faded and been cleared. */
  onDecayed?: () => void;
};

let activeMoodOverlay: MoodOverlayHandle | null = null;
let moodOverrides: Record<string, { blendshapes?: Record<string, number>; intensity?: number; enabled?: boolean; absoluteBlendshapes?: boolean }> = {};
let moodIntensityScale = 1;
let omittedMoodKeys = new Set<string>();
// Personality knobs (Aug 14 2026): a global expression scale, per-mood
// multipliers, and additive resting-face offsets for the neutral mood.
// They COMPOSE with the per-model moodIntensityScale (personality × model),
// never replace it — see getEffectiveMoodOverlayScale.
let personalityMoodScale = 1;
let personalityPerMoodScale: Record<string, number> = {};
let personalityRestingFace: Record<string, number> = {};

/**
 * Set per-model mood overrides that merge on top of the hardcoded MOOD_STATES.
 * Called once when the avatar loads its resolved mood config from the schema-service.
 */
export function setMoodOverrides(overrides: Record<string, { blendshapes?: Record<string, number>; intensity?: number; enabled?: boolean; absoluteBlendshapes?: boolean }>) {
  moodOverrides = overrides ?? {};
  console.log('🎭 Mood overrides applied:', Object.keys(moodOverrides).length, 'moods');
}

/**
 * Set the per-model mood-intensity multiplier. Applied at overlay time as a
 * multiplier on each mood's `intensity` field. Sam ships with 0.55 because
 * the legacy default mesh exaggerates frown blendshapes; other models default
 * to 1 (no change). Sourced from ModelConfig.moodIntensity in modelRegistry.
 *
 * Called from Avatar.tsx when the active model is resolved. Pass 1 (or omit)
 * to reset; pass undefined to leave the previous value in place.
 *
 * Slice 6 of the avatar-fixes plan, May 11 2026.
 */
export function setMoodIntensityScale(scale: number | undefined) {
  if (typeof scale !== 'number' || !Number.isFinite(scale)) {
    return;
  }
  const next = clamp01(scale);
  if (next === moodIntensityScale) {
    return;
  }
  moodIntensityScale = next;
  console.log('🎭 Mood intensity scale set:', moodIntensityScale);
}

/** Read-only accessor used by tests + Avatar Editor preview. */
export function getMoodIntensityScale(): number {
  return moodIntensityScale;
}

/** Personality scales may exceed 1 (e.g. bubbly 1.1×); cap at 2 for sanity. */
function clampPersonalityScale(value: number): number {
  return Math.max(0, Math.min(2, value));
}

/**
 * Set the personality expression scales. `scale` is the global
 * multiplier; `perMood` layers per-mood multipliers on top. Pass
 * (1, {}) — or call with no personality — to reset.
 * Called from Avatar.tsx's `avatar:personality:configure` handler.
 */
export function setPersonalityMoodScales(scale?: number, perMood?: Record<string, number>): void {
  personalityMoodScale = clampPersonalityScale(
    typeof scale === 'number' && Number.isFinite(scale) ? scale : 1,
  );
  personalityPerMoodScale = {};
  for (const [mood, value] of Object.entries(perMood ?? {})) {
    const n = Number(value);
    if (Number.isFinite(n)) personalityPerMoodScale[mood] = clampPersonalityScale(n);
  }
  console.log('🎭 personality mood scales set:', personalityMoodScale, personalityPerMoodScale);
}

/**
 * Additive blendshape offsets merged into the NEUTRAL mood baseline
 * (the personality's resting face). Pass undefined/empty to clear.
 */
export function setPersonalityRestingFace(offsets?: Record<string, number>): void {
  personalityRestingFace = {};
  for (const [key, value] of Object.entries(offsets ?? {})) {
    const n = Number(value);
    if (Number.isFinite(n)) personalityRestingFace[key] = Math.max(-1, Math.min(1, n));
  }
  if (Object.keys(personalityRestingFace).length > 0) {
    console.log('🎭 personality resting face set:', personalityRestingFace);
  }
}

/**
 * The composed overlay scale for a mood: per-model moodIntensityScale ×
 * personality global scale × personality per-mood scale. Absolute
 * (author-exact) overrides bypass all scaling, matching the pre-existing
 * modelScale behaviour. Exported for tests.
 */
export function getEffectiveMoodOverlayScale(mood: string, absoluteBlendshapes = false): number {
  if (absoluteBlendshapes) return 1;
  const perMood = personalityPerMoodScale[mood] ?? 1;
  return clampPersonalityScale(moodIntensityScale * personalityMoodScale * perMood);
}

/**
 * Morph names that this model must not receive from MOOD_STATES.
 * Sam's legacy `mouthClose` / `mouthPress` raise the lower lip over the
 * upper (ARKit close/press on a CC rest pose). Omit them per-model rather
 * than editing global MOOD_STATES, which would flatten Cara/GymGirl concern.
 */
export function setOmittedMoodBlendshapes(names: string[] | undefined): void {
  const next = new Set<string>();
  for (const name of names ?? []) {
    for (const alias of expandMoodMorphAliases(name)) {
      next.add(alias);
    }
  }
  omittedMoodKeys = next;
  if (next.size > 0) {
    console.log('🎭 Omitted mood blendshapes:', [...next].join(', '));
  }
}

export function getOmittedMoodBlendshapes(): string[] {
  return [...omittedMoodKeys];
}

function expandMoodMorphAliases(name: string): string[] {
  const aliases = new Set<string>([name]);
  if (name.endsWith('_L')) aliases.add(name.replace(/_L$/, 'Left'));
  if (name.endsWith('_R')) aliases.add(name.replace(/_R$/, 'Right'));
  if (name.endsWith('Left')) aliases.add(name.replace(/Left$/, '_L'));
  if (name.endsWith('Right')) aliases.add(name.replace(/Right$/, '_R'));
  const bilateral = ['mouthPress', 'mouthFrown', 'mouthSmile', 'mouthDimple', 'mouthStretch', 'mouthLowerDown', 'mouthUpperUp'];
  if (bilateral.includes(name)) {
    aliases.add(`${name}_L`);
    aliases.add(`${name}_R`);
    aliases.add(`${name}Left`);
    aliases.add(`${name}Right`);
  }
  return [...aliases];
}

function stripOmittedBlendshapes(blendshapes: Record<string, number>): Record<string, number> {
  if (omittedMoodKeys.size === 0) {
    return blendshapes;
  }
  const next: Record<string, number> = { ...blendshapes };
  // Pin omitted keys to 0 instead of dropping them. Dropping lets idle mixer
  // tracks keep driving Sam's invert-family morphs (mouthClose / frown /
  // shrugLower) — which is why omitting them from concerned still bunched
  // the lower lip over the upper. Overlay tick() absolute-sets these.
  for (const key of omittedMoodKeys) {
    next[key] = 0;
  }
  return next;
}

function getResolvedMoodState(mood: Sentiment) {
  const base = MOOD_STATES[mood];
  if (!base) return null;
  const override = moodOverrides[mood];
  const merged = override
    ? {
        ...base,
        blendshapes: { ...base.blendshapes, ...(override.blendshapes ?? {}) },
        intensity: override.intensity ?? base.intensity,
      }
    : base;
  // Personality resting face: additive offsets on the NEUTRAL baseline.
  // Applied BEFORE the omitted-key strip so per-model omits stay authoritative.
  let blendshapes = merged.blendshapes;
  if (mood === 'neutral' && Object.keys(personalityRestingFace).length > 0) {
    blendshapes = { ...blendshapes };
    for (const [key, offset] of Object.entries(personalityRestingFace)) {
      blendshapes[key] = clamp01((Number(blendshapes[key]) || 0) + offset);
    }
  }
  return {
    ...merged,
    blendshapes: stripOmittedBlendshapes(blendshapes),
    absoluteBlendshapes: Boolean(override?.absoluteBlendshapes),
  };
}

/** Test helper: resolved mood weights after per-model omit + overrides. */
export function getResolvedMoodBlendshapes(mood: Sentiment): Record<string, number> | null {
  return getResolvedMoodState(mood)?.blendshapes ?? null;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function easeOutCubic(t: number): number {
  const normalized = clamp01(t);
  return 1 - Math.pow(1 - normalized, 3);
}

/**
 * Staggered ramp-in for mood morphs (Aug 17 2026, Keith's "dark bits flash
 * as a smile forms" builder report).
 *
 * When every morph ramps on the same curve, jawOpen reaches a visible gap
 * before mouthSmile / mouthUpperUp have lifted the lips clear of it, so
 * mid-transition frames transiently expose the dark inner-lip band. The fix
 * is ordering, not amplitude: lip-lift and corner-pull morphs ('lead')
 * complete early in the ramp, jaw-open morphs ('lag') start after a delay
 * and complete exactly at the ramp end. Total transition duration is
 * unchanged, targets are unchanged, and the stagger is name-based so it is
 * model-agnostic. Decay-out is NOT staggered — the fade multiplies all
 * morphs uniformly, and closing the jaw ahead of the smile is the safe
 * direction anyway.
 */
export type MoodMorphStagger = 'lead' | 'normal' | 'lag';

const MOOD_STAGGER_LEAD_FRACTION = 0.6;
const MOOD_STAGGER_LAG_DELAY_FRACTION = 0.35;

export function classifyMoodMorphStagger(name: string): MoodMorphStagger {
  const lower = name.toLowerCase();
  if (lower.includes('jawopen') || lower.includes('mouthopen') || lower.includes('jawforward')) return 'lag';
  if (lower.includes('mouthsmile') || lower.includes('mouthupperup')) return 'lead';
  return 'normal';
}

/**
 * Attack weight (0–1) for a morph class at `elapsedMs` into a ramp of
 * `durationMs`. All classes reach exactly 1 by `durationMs`.
 */
export function moodStaggerAttackWeight(
  stagger: MoodMorphStagger,
  elapsedMs: number,
  durationMs: number
): number {
  const duration = Math.max(1, durationMs);
  if (stagger === 'lead') {
    return easeOutCubic(elapsedMs / (duration * MOOD_STAGGER_LEAD_FRACTION));
  }
  if (stagger === 'lag') {
    const delay = duration * MOOD_STAGGER_LAG_DELAY_FRACTION;
    return easeOutCubic((elapsedMs - delay) / (duration - delay));
  }
  return easeOutCubic(elapsedMs / duration);
}

function getMixerRoot(mixer: THREE.AnimationMixer): THREE.Object3D | null {
  const maybeGetRoot = (mixer as unknown as { getRoot?: () => THREE.Object3D }).getRoot;
  if (typeof maybeGetRoot === 'function') {
    return maybeGetRoot.call(mixer);
  }
  return ((mixer as unknown as { _root?: THREE.Object3D })._root) ?? null;
}

function isGenericBodyMorphKey(key: string): boolean {
  return /^body_morph_\d+$/i.test(key) || /^target_\d+$/i.test(key);
}

function hasNamedFacialMorphs(keys: string[]): boolean {
  return keys.some((key) => {
    const normalized = key.toLowerCase();
    return normalized.includes('jaw') ||
      normalized.includes('mouth') ||
      normalized.includes('tongue') ||
      normalized.includes('cheek') ||
      normalized.includes('brow') ||
      normalized.includes('eye') ||
      normalized.includes('nose');
  });
}

/**
 * Speech-restore donors (Sam `Mesh002` teeth, HG_TeethLower, tongue, lashes).
 * They expose jaw/mouth morph names, so a naive "has facial morphs" check
 * treats them as face meshes. Applying ARKit mood weights there maps onto
 * viseme shapes and slams the jaw open while the body mesh also tries to
 * smile/frown — the "huge mouth + mood at once" bug on Sam.
 */
export function isMoodSpeechDonorMesh(mesh: any, bodyMeshName?: string): boolean {
  const meshName = String(mesh?.name || '');
  const lower = meshName.toLowerCase();
  if (/teeth|tooth|tongue|eyelash|lash/.test(lower)) {
    return true;
  }
  if (bodyMeshName && meshName !== bodyMeshName && /^mesh\d+$/i.test(meshName)) {
    return true;
  }
  return false;
}

export function isMoodCandidateMesh(mesh: any, bodyMeshName: string | undefined): boolean {
  if (!mesh?.morphTargetInfluences || !mesh?.morphTargetDictionary) {
    return false;
  }

  const morphKeys = Object.keys(mesh.morphTargetDictionary);
  if (morphKeys.length === 0) {
    return false;
  }

  if (isMoodSpeechDonorMesh(mesh, bodyMeshName)) {
    return false;
  }

  if (bodyMeshName && mesh.name === bodyMeshName) {
    return true;
  }

  const meshName = String(mesh.name || '').toLowerCase();
  const isFaceNamedMesh =
    meshName.includes('head') ||
    meshName.includes('face') ||
    meshName.includes('body') ||
    meshName.includes('mouth');
  const hasOnlyGenericBodyMorphs = morphKeys.every(isGenericBodyMorphKey);

  if (bodyMeshName && mesh.name !== bodyMeshName) {
    // Primary face mesh is known. Only also overlay named head/face/body
    // siblings (UE5 submeshes). Do not overlay unnamed viseme donors just
    // because they happen to list jawOpen in their morph dictionary.
    return isFaceNamedMesh && hasNamedFacialMorphs(morphKeys) && !hasOnlyGenericBodyMorphs;
  }

  if (hasNamedFacialMorphs(morphKeys)) {
    return true;
  }

  return isFaceNamedMesh && !hasOnlyGenericBodyMorphs;
}

/**
 * One smile writer per corner. Driving Left AND _L AND *old as
 * independent morphs stacks into the face-splitting grin (thinking /
 * listening / idle / startup, Aug 29 2026). Prefer live ARKit names.
 */
type MoodMorphTarget = { idx: number; value: number; stagger: MoodMorphStagger };

function applyExclusiveSmileTargets(
  dict: Record<string, number>,
  blendshapes: Record<string, number>,
  targets: MoodMorphTarget[]
): void {
  const smileIdxs = new Set<number>();
  for (const name of Object.keys(dict)) {
    if (!isSmileMorphName(name)) continue;
    smileIdxs.add(dict[name]);
  }
  for (let i = targets.length - 1; i >= 0; i--) {
    if (smileIdxs.has(targets[i].idx)) targets.splice(i, 1);
  }
  const smileWeight = smileWeightFromBlendshapes(blendshapes);
  if (smileWeight < 0.01) return;
  const slots = pickExclusiveSmileSlots(dict);
  const seen = new Set(targets.map((target) => target.idx));
  for (const idx of [slots.left, slots.right]) {
    if (idx === undefined || seen.has(idx)) continue;
    targets.push({ idx, value: clamp01(smileWeight), stagger: 'lead' });
    seen.add(idx);
  }
}

function collectMoodMeshes(
  mixer: THREE.AnimationMixer,
  blendshapes: Record<string, number>,
  bodyMeshName?: string
): Array<{
  mesh: THREE.Mesh & { morphTargetInfluences: number[]; morphTargetDictionary: Record<string, number> };
  targets: MoodMorphTarget[];
}> {
  const root = getMixerRoot(mixer);
  if (!root) {
    return [];
  }

  const moodMeshes: Array<{
    mesh: THREE.Mesh & { morphTargetInfluences: number[]; morphTargetDictionary: Record<string, number> };
    targets: MoodMorphTarget[];
  }> = [];

  root.traverse((node: any) => {
    if (!isMoodCandidateMesh(node, bodyMeshName)) {
      return;
    }

    const targets: MoodMorphTarget[] = [];
    for (const [key, value] of Object.entries(blendshapes)) {
      const idx = resolveMorphTargetIndex(node.morphTargetDictionary, key);
      if (idx !== undefined && idx < node.morphTargetInfluences.length) {
        targets.push({ idx, value: clamp01(Number(value) || 0), stagger: classifyMoodMorphStagger(key) });
      }
    }
    applyExclusiveSmileTargets(node.morphTargetDictionary, blendshapes, targets);

    if (targets.length > 0) {
      moodMeshes.push({ mesh: node, targets });
    }
  });

  return moodMeshes;
}

function startMoodOverlay(
  mood: Sentiment,
  mixer: THREE.AnimationMixer,
  blendshapes: Record<string, number>,
  transitionDuration: number,
  bodyMeshName?: string,
  intensity = 1,
  absoluteBlendshapes = false,
  decay?: MoodOverlayDecayOptions
): MoodOverlayHandle | null {
  const moodMeshes = collectMoodMeshes(mixer, blendshapes, bodyMeshName);
  if (moodMeshes.length === 0) {
    console.warn('[moodAnimator] No compatible morph mesh found for mood:', mood);
    return null;
  }

  let rafId: number | null = null;
  let disposed = false;
  let decayedNotified = false;
  const startTime = performance.now();
  const durationMs = Math.max(80, transitionDuration || 400);
  const decayAfterMs = typeof decay?.decayAfterMs === 'number' && decay.decayAfterMs > 0
    ? decay.decayAfterMs
    : null;
  const decayDurationMs = Math.max(80, decay?.decayDurationMs ?? 1500);
  // When the fade-out begins and how long it takes. `null` fadeStartAt means
  // the overlay is pinned (no decay options AND no early fade requested).
  // `beginEarlyFade` — the user started talking — pulls the start forward.
  let fadeStartAt: number | null = decayAfterMs === null
    ? null
    : startTime + durationMs + decayAfterMs;
  let fadeDurationEffectiveMs = decayDurationMs;
  const appliedValues = new WeakMap<THREE.Mesh, Map<number, number>>();
  // Per-morph-class staggered attack × optional hold-then-fade envelope.
  // Without decay the envelope stays pinned at the attack value — the
  // historical behaviour. The fade multiplies all classes uniformly.
  const attackWeightFor = (stagger: MoodMorphStagger, now: number) =>
    moodStaggerAttackWeight(stagger, now - startTime, durationMs);
  const fadeFactor = (now: number) => {
    if (fadeStartAt === null) return 1;
    if (now <= fadeStartAt) return 1;
    return 1 - easeOutCubic((now - fadeStartAt) / fadeDurationEffectiveMs);
  };
  const isFullyDecayed = (now = performance.now()) =>
    fadeStartAt !== null && now >= fadeStartAt + fadeDurationEffectiveMs;
  // Model scale × personality scales (may exceed 1; morph values clamp
  // to [0,1] at write time). Snapshotted at overlay start.
  const overlayScale = getEffectiveMoodOverlayScale(mood, absoluteBlendshapes);
  // Expressiveness cap (sentiment settings, Aug 17 2026): a hard per-morph
  // ceiling applied AFTER the model × personality scaling above, so a
  // personality multiplier > 1 can never push a mood morph past the cap.
  // Default 1.0 is a no-op (morph writes already clamp to [0,1]).
  const overlayCap = clamp01(getSentimentExpressivenessCap());

  const applyAt = (now: number) => {
    const fade = fadeFactor(now);

    for (const { mesh, targets } of moodMeshes) {
      const influences = mesh.morphTargetInfluences;
      if (!influences) {
        continue;
      }

      let appliedForMesh = appliedValues.get(mesh);
      if (!appliedForMesh) {
        appliedForMesh = new Map<number, number>();
        appliedValues.set(mesh, appliedForMesh);
      }

      for (const { idx, value, stagger } of targets) {
        const weight = clamp01(attackWeightFor(stagger, now) * fade) * clamp01(intensity) * overlayScale;
        const previousOverlay = appliedForMesh.get(idx) ?? 0;
        const nextOverlay = Math.min(value * weight, overlayCap);
        influences[idx] = clamp01((influences[idx] ?? 0) - previousOverlay + nextOverlay);
        if (nextOverlay < 0.001) {
          appliedForMesh.delete(idx);
        } else {
          appliedForMesh.set(idx, nextOverlay);
        }
      }
    }
  };

  const clearOverlay = () => {
    for (const { mesh } of moodMeshes) {
      const influences = mesh.morphTargetInfluences;
      const appliedForMesh = appliedValues.get(mesh);
      if (!influences || !appliedForMesh) {
        continue;
      }

      appliedForMesh.forEach((value, idx) => {
        influences[idx] = clamp01((influences[idx] ?? 0) - value);
      });
      appliedForMesh.clear();
    }
  };

  // Fully faded: clear our writes, stop the loop, notify the caller once.
  const finishDecay = () => {
    if (disposed) {
      return;
    }
    disposed = true;
    if (rafId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    clearOverlay();
    if (!decayedNotified) {
      decayedNotified = true;
      decay?.onDecayed?.();
    }
  };

  const animate = (now: number) => {
    if (disposed) {
      return;
    }

    if (isFullyDecayed(now)) {
      finishDecay();
      return;
    }
    applyAt(now);
    rafId = requestAnimationFrame(animate);
  };

  // Guarded for non-browser (unit-test) environments; tick() drives there.
  if (typeof requestAnimationFrame === 'function') {
    rafId = requestAnimationFrame(animate);
  }

  return {
    mood,
    blendshapes,
    hasDecay: decayAfterMs !== null,
    beginEarlyFade(fadeDurationMs?: number) {
      if (disposed) {
        return;
      }
      const now = performance.now();
      // Already fading (scheduled fade reached, or an earlier early-fade).
      if (fadeStartAt !== null && now >= fadeStartAt) {
        return;
      }
      fadeStartAt = now;
      fadeDurationEffectiveMs = Math.max(80, fadeDurationMs ?? 400);
    },
    /**
     * Re-apply after AnimationMixer.update(). Mixer idle tracks bind the
     * body mesh and zero the overlay's previous write; subtract-then-add
     * accounting then becomes a no-op. Absolute-set the mood-owned indices
     * so the expression survives the mixer without doubling.
     */
    tick() {
      if (disposed) {
        return;
      }
      if (isFullyDecayed()) {
        finishDecay();
        return;
      }
      const now = performance.now();
      const fade = fadeFactor(now);
      for (const { mesh, targets } of moodMeshes) {
        const influences = mesh.morphTargetInfluences;
        if (!influences) {
          continue;
        }
        let appliedForMesh = appliedValues.get(mesh);
        if (!appliedForMesh) {
          appliedForMesh = new Map<number, number>();
          appliedValues.set(mesh, appliedForMesh);
        }
        for (const { idx, value, stagger } of targets) {
          const weight = clamp01(attackWeightFor(stagger, now) * fade) * clamp01(intensity) * overlayScale;
          const nextOverlay = Math.min(value * weight, overlayCap);
          influences[idx] = clamp01(nextOverlay);
          if (nextOverlay < 0.001) {
            appliedForMesh.delete(idx);
          } else {
            appliedForMesh.set(idx, nextOverlay);
          }
        }
      }
    },
    stop() {
      disposed = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      clearOverlay();
    }
  };
}

/**
 * Editor / settings-modal preview path: apply an arbitrary blendshape set
 * as the same subtract-then-add overlay used by named moods.
 *
 * Do NOT drive this through `createAnimation` + AdditiveAnimationBlendMode.
 * A single keyframe at t=0 produces a 0-duration clip; looping it makes
 * `action.time % 0 === NaN` and slams morphs (typically jaw fully open).
 */
export function applyBlendshapeOverlay(
  mixer: THREE.AnimationMixer,
  blendshapes: Record<string, number>,
  transitionDuration: number,
  bodyMeshName?: string,
  intensity = 1
): { stop: () => void } | null {
  if (!mixer || !blendshapes || Object.keys(blendshapes).length === 0) {
    return null;
  }

  activeMoodOverlay?.stop();
  activeMoodOverlay = startMoodOverlay(
    'neutral',
    mixer,
    stripOmittedBlendshapes(blendshapes),
    transitionDuration,
    bodyMeshName,
    intensity
  );
  return activeMoodOverlay;
}

export function stopMoodOverlay(): void {
  activeMoodOverlay?.stop();
  activeMoodOverlay = null;
}

/**
 * Fade the active SENTIMENT mood overlay out early (~fadeDurationMs), e.g.
 * because the user just started talking: a held smile from the previous
 * turn's sentiment should not stay pinned on the face while the avatar is
 * supposed to read attentive-neutral (Aug 17 2026, Visual IVR "smile hanging
 * around while I talk" defect). Only overlays applied WITH decay options are
 * affected — Avatar Editor previews are pinned on purpose and stay pinned.
 *
 * Returns true when an overlay accepted the early fade.
 */
export function fadeOutActiveSentimentMood(fadeDurationMs = 400): boolean {
  if (!activeMoodOverlay || !activeMoodOverlay.hasDecay) {
    return false;
  }
  activeMoodOverlay.beginEarlyFade(fadeDurationMs);
  return true;
}

/** Call from Avatar useFrame after mixer.update, while not speaking. */
export function tickMoodOverlay(): void {
  activeMoodOverlay?.tick();
}

export function getActiveMoodOverlayBlendshapes(): Record<string, number> | null {
  return activeMoodOverlay?.blendshapes ?? null;
}

export async function applyMoodToAvatar(
  _avatarRef: any,
  mood: Sentiment,
  currentMood: Sentiment,
  transitionDuration: number,
  morphTargetDictionary: any,
  mixer: THREE.AnimationMixer,
  bodyMeshName?: string,
  decay?: MoodOverlayDecayOptions
): Promise<void> {
  
  if (!morphTargetDictionary || !mixer) {
    console.warn('Cannot apply mood - missing dependencies');
    return;
  }

  const moodState = getResolvedMoodState(mood);
  if (!moodState) {
    console.warn('Unknown mood state:', mood);
    return;
  }

  try {
    activeMoodOverlay?.stop();
    activeMoodOverlay = startMoodOverlay(
      mood,
      mixer,
      moodState.blendshapes,
      transitionDuration,
      bodyMeshName,
      (moodState as unknown as { intensity?: number }).intensity ?? 1,
      Boolean((moodState as unknown as { absoluteBlendshapes?: boolean }).absoluteBlendshapes),
      decay
    );

    if (activeMoodOverlay) {
      const nonzero = Object.fromEntries(
        Object.entries(moodState.blendshapes).filter(([, v]) => Math.abs(Number(v) || 0) > 0.001)
      );
      const pinned = Object.fromEntries(
        Object.entries(moodState.blendshapes).filter(([, v]) => Number(v) === 0)
      );
      console.log('Mood overlay playing:', mood, 'on mesh:', bodyMeshName || '(auto)', {
        nonzero,
        pinned: Object.keys(pinned),
      });
    }
  } catch (error) {
    console.error('Error applying mood:', error);
  }
}

/**
 * Get current mood intensity based on conversation context
 * @param recentSentiments - Array of recent sentiment detections
 * @returns Intensity multiplier (0-1)
 */
export function calculateMoodIntensity(recentSentiments: Array<{ sentiment: Sentiment; confidence: number }>): number {
  if (recentSentiments.length === 0) return 0.5;

  // Average confidence of recent sentiments
  const avgConfidence = recentSentiments.reduce((sum, s) => sum + s.confidence, 0) / recentSentiments.length;

  // Higher confidence = stronger mood expression
  return Math.min(avgConfidence * 1.2, 1.0);
}

